/**
 * Design tokens for consistent UI sizing and styling.
 * Use these constants for a unified design language across components.
 */

/**
 * Compact UI element sizing (Linear-inspired density)
 * Use for property badges, tags, inline selects, type indicators
 *
 * Mobile: h-6 (24px) for touch targets, text-sm for readability
 * Desktop: h-4 (16px) for density, text-sm for consistency
 */
export const COMPACT_SIZES = {
  /** Height for compact elements - responsive for touch */
  height: 'h-6 sm:h-4',
  /** Horizontal padding for compact elements */
  paddingX: 'px-1.5',
  /** Gap between icon and text - tighter for Linear aesthetic */
  gap: 'gap-0.5',
  /** Border radius for compact elements - sharper per Linear */
  rounded: 'rounded-sm',
  /** Font size for compact elements */
  fontSize: 'text-sm',
  /** Icon size for compact elements (12px) */
  iconSize: 'h-3 w-3',
} as const

/**
 * Pre-composed class strings for compact UI patterns
 * Linear-inspired: Sharp corners, tight spacing, responsive sizing
 * Mobile: h-6 (24px) for touch, Desktop: h-4 (16px) for density
 */
export const COMPACT_CLASSES = {
  /** Base classes shared by all compact elements */
  base: 'inline-flex items-center font-medium transition-colors',

  /** Layout classes for compact elements - responsive for touch */
  layout: 'h-6 gap-0.5 rounded-sm px-1.5 text-sm sm:h-4',

  /** Active/filled state (selected items) */
  active: 'bg-foreground/8 text-foreground',

  /** Inactive state (placeholders, add buttons) */
  inactive: 'text-muted-foreground',

  /** Hover state for inactive elements */
  inactiveHover: 'hover:bg-muted/30 hover:text-foreground',

  /** Combined: base + layout + active */
  filled:
    'inline-flex h-6 items-center gap-0.5 rounded-sm px-1.5 text-sm font-medium bg-foreground/8 text-foreground sm:h-4',

  /** Combined: base + layout + inactive */
  dashed:
    'inline-flex h-6 items-center gap-0.5 rounded-sm px-1.5 text-sm font-medium text-muted-foreground hover:bg-muted/30 hover:text-foreground sm:h-4',
} as const

/**
 * Icon sizing tokens
 */
export const ICON_SIZES = {
  /** Compact icon (12px) - for compact badges/tags */
  compact: 'h-3 w-3',
  /** Small icon (12px) - for small buttons, list items */
  small: 'h-3 w-3',
  /** Default icon (14px) - for buttons, inputs */
  default: 'h-3.5 w-3.5',
  /** Medium icon (16px) - for larger buttons */
  medium: 'h-4 w-4',
  /** Large icon (20px) - for empty states, headers */
  large: 'h-5 w-5',
} as const

/**
 * Text styling tokens for metadata and secondary content
 */
export const TEXT_STYLES = {
  /** Compact metadata text */
  metaCompact: 'text-muted-foreground text-sm',
  /** Small metadata text */
  metaSmall: 'text-muted-foreground text-xs',
  /** Faded metadata (lower contrast) */
  metaFaded: 'text-muted-foreground/60 text-xs',
  /** Section header styling */
  sectionHeader:
    'text-muted-foreground text-xs font-medium tracking-wide uppercase',
} as const

/**
 * Focus ring tokens for accessibility
 */
export const FOCUS_STYLES = {
  /** Default focus ring */
  ring: 'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
  /** Compact focus ring (smaller offset) */
  ringCompact:
    'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none',
} as const

/**
 * Select trigger styling for compact property selects
 * Linear-inspired: Sharp corners, responsive sizing for touch
 */
export const SELECT_TRIGGER_CLASSES = {
  /** Filled/active select trigger - responsive sizing */
  filled:
    'inline-flex items-center bg-foreground/8 text-foreground h-6 w-auto gap-1 rounded-sm px-1.5 text-sm font-medium transition-colors hover:bg-foreground/12 sm:h-4',
  /** Inactive select trigger - responsive sizing */
  dashed:
    'inline-flex items-center text-muted-foreground h-6 w-auto gap-1 rounded-sm px-1.5 text-sm transition-colors hover:text-foreground hover:bg-muted/30 sm:h-4',
} as const

/**
 * Maximum visible items on mobile before showing overflow
 */
export const MOBILE_OVERFLOW = {
  /** Max items to show on mobile before "+N more" */
  maxVisible: 2,
} as const
